import { Component } from '@theme/component';
import { updateAllHeaderCustomProperties } from '@theme/utilities';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * RAF ID for scroll handler throttling.
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Tracks whether the announcement section is currently hidden.
   * @type {boolean}
   */
  #hidden = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('click', this.#handleClick);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    if (this.hideOnScroll) {
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      this.#updateScrollState();
    }

    this.current = 0;
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || (this.refs.slides ?? []).length < 2) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get hideOnScroll() {
    return this.dataset.hideOnScroll === 'true';
  }

  get scrollThreshold() {
    const threshold = parseInt(this.dataset.scrollThreshold || '0', 10);
    if (Number.isNaN(threshold)) return 0;
    return threshold;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slideCount = (this.refs.slides ?? []).length;
    if (slideCount === 0) return;

    this.#current = current;

    let relativeIndex = current % slideCount;
    if (relativeIndex < 0) {
      relativeIndex += slideCount;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-email-popup-trigger]');
    if (!(trigger instanceof HTMLAnchorElement)) return;

    const popup = document.querySelector('grow-email-popup');
    if (!(popup instanceof HTMLElement) || typeof popup.showPopup !== 'function') return;

    const didOpen = popup.showPopup();
    if (!didOpen) return;

    event.preventDefault();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState() {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#setHidden(scrollTop > this.scrollThreshold);
  }

  #setHidden(hidden) {
    if (this.#hidden === hidden) return;

    const section = this.closest('.announcement-bar');
    if (!(section instanceof HTMLElement)) return;

    this.#hidden = hidden;
    section.classList.toggle('is-hidden', hidden);
    updateAllHeaderCustomProperties();
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
